import type { ConfirmationCardProps } from "../../../types/ChatCard";
import { AccessGroupConfirmationCard } from "./ConfirmationCards/AccessGroupConfirmationCard";
import { CommunityConfirmationCard } from "./ConfirmationCards/CommunityConfirmationCard";
import { CommunitySegmentConfirmationCard } from "./ConfirmationCards/CommunitySegmentConfirmationCard";
import { InviteLinkConfirmationCard } from "./ConfirmationCards/InviteLinkConfirmationCard";
import { MemberConfirmationCard } from "./ConfirmationCards/MemberConfirmationCard";
import { ProfileFieldConfirmationCard } from "./ConfirmationCards/ProfileFieldConfirmationCard";
import { SpaceConfirmationCard } from "./ConfirmationCards/SpaceConfirmationCard";
import { SpaceGroupConfirmationCard } from "./ConfirmationCards/SpaceGroupConfirmationCard";
import { TagConfirmationCard } from "./ConfirmationCards/TagConfirmationCard";
import { UserProfileCard } from "./ConfirmationCards/UserProfileCard";

type ComponentRenderer = (params: Record<string, unknown>) => JSX.Element;

const CONFIRMATION_CARD_COMPONENTS: Record<string, ComponentRenderer> = {
  community: params => (
    <CommunityConfirmationCard
      name={params.name as string}
      locale={params.locale as string}
      is_private={params.is_private as boolean}
      desktop_app_enabled={params.desktop_app_enabled as boolean}
      brand_color={params.brand_color as string}
      brand_dark_color={params.brand_dark_color as string}
      reply_to_email={params.reply_to_email as string}
      community_switcher_enabled={params.community_switcher_enabled as boolean}
      deactivate_account_enabled={params.deactivate_account_enabled as boolean}
      allow_profile_search_indexing={
        params.allow_profile_search_indexing as boolean
      }
      truncate_post_body_in_email_notifications={
        params.truncate_post_body_in_email_notifications as boolean
      }
      custom_cta_for_share_links={params.custom_cta_for_share_links as boolean}
      default_search_sorting={params.default_search_sorting as string}
      hide_emails_on_member_profiles={
        params.hide_emails_on_member_profiles as boolean
      }
      allow_members_to_go_live={params.allow_members_to_go_live as boolean}
      allow_moderator_csv_download={
        params.allow_moderator_csv_download as boolean
      }
      ios_app_enabled={params.ios_app_enabled as boolean}
      show_ios_app_banner={params.show_ios_app_banner as boolean}
      weekly_digest_enabled={params.weekly_digest_enabled as boolean}
      messaging_enabled={params.messaging_enabled as boolean}
      member_to_member_messaging_enabled={
        params.member_to_member_messaging_enabled as boolean
      }
      group_messaging_enabled={params.group_messaging_enabled as boolean}
      voice_messages_enabled={params.voice_messages_enabled as boolean}
      legal_email={params.legal_email as string}
      legal_address={params.legal_address as string}
      hide_circle_discover={params.hide_circle_discover as boolean}
      white_label={params.white_label as boolean}
      enforce_otp_for_signup={params.enforce_otp_for_signup as boolean}
      content_copilot_enabled={params.content_copilot_enabled as boolean}
      activity_scores_enabled={params.activity_scores_enabled as boolean}
      ai_summaries_enabled={params.ai_summaries_enabled as boolean}
      sso_enabled={params.sso_enabled as boolean}
      enable_state_param={params.enable_state_param as boolean}
      custom_sign_up={params.custom_sign_up as boolean}
    />
  ),
  community_member: params => (
    <MemberConfirmationCard
      name={params.name as string}
      email={params.email as string}
    />
  ),
  space_group: params => (
    <SpaceGroupConfirmationCard
      name={params.name as string}
      member_count={params.member_count as number}
    />
  ),
  space: params => (
    <SpaceConfirmationCard
      name={params.name as string}
      space_group={params.space_group as string}
      type={params.type as string}
      access={params.access as string}
    />
  ),
  invitation_link: params => (
    <InviteLinkConfirmationCard
      name={params.name as string}
      status={params.status as string}
      members_joined_count={params.members_joined_count as number}
    />
  ),
  member_tag: params => (
    <TagConfirmationCard
      name={params.name as string}
      is_public={params.is_public as boolean}
    />
  ),
  access_group: params => (
    <AccessGroupConfirmationCard
      name={params.name as string}
      description={params.description as string}
      member_count={params.member_count as number}
    />
  ),
  profile_field: params => (
    <ProfileFieldConfirmationCard
      label={params.label as string}
      field_type={params.field_type as string}
    />
  ),
  community_segment: params => (
    <CommunitySegmentConfirmationCard
      title={params.title as string}
      people_count={params.people_count as number}
    />
  ),
  user_profile: params => (
    <UserProfileCard
      params={params as Record<string, string | boolean | string[]>}
    />
  ),
};

export const ConfirmationCard = ({
  params,
  resource_class,
}: ConfirmationCardProps) => {
  if (!resource_class) {
    return null;
  }

  const renderer = CONFIRMATION_CARD_COMPONENTS[resource_class];
  if (!renderer) {
    return null;
  }

  return renderer(params as Record<string, unknown>);
};
